import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import { useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet } from 'react-native';
import { colors as CropCircleColors } from '../crop-circle/tokens';
import { DebouncedTextInput } from '../forms';

type EntitySearchInputProps = {
  filter: { keyword?: string } | undefined | null;
  onSearch: (keyword: string) => Promise<void>;
  placeholder: string;
  onBlur?: () => void;
  /**
   * Pass autoFocus={!isLoading && !filter?.keyword} to opt in to auto-focusing
   * when there is no pre-existing keyword (e.g. Assets).
   */
  autoFocus?: boolean;
  /**
   * When true, prevents the search from firing when the keyword is restored
   * from persisted storage on initial mount. Use for pages where an initial
   * empty-to-keyword transition should not re-trigger a query (e.g. Routines).
   */
  disableInitialSearch?: boolean;
};

export const EntitySearchInput = ({
  filter,
  onSearch,
  placeholder,
  onBlur,
  autoFocus = false,
  disableInitialSearch = false,
}: EntitySearchInputProps) => {
  const isCropCircleRedesign = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const [text, setText] = useState('');
  const hasTyped = useRef(!disableInitialSearch);

  useEffect(() => {
    if (!text && filter?.keyword) {
      setText(filter.keyword);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.keyword]);

  const handleSearch = async (keyword: string) => {
    if (!hasTyped.current) return;
    await onSearch(keyword);
  };

  return (
    filter && (
      <DebouncedTextInput
        debounceTimeMs={500}
        debounceFunc={handleSearch}
        placeholder={placeholder}
        value={text}
        onBlur={() => {
          if (!text && onBlur) onBlur();
        }}
        onChangeText={(value) => {
          if (!hasTyped.current) hasTyped.current = true;
          setText(value);
        }}
        height={isCropCircleRedesign ? 32 : 44}
        width={'100%'}
        outlineStyle={{
          ...styles.inputStyle,
          backgroundColor:
            isCropCircleRedesign || Platform.OS !== 'web'
              ? CropCircleColors.neutral[200]
              : 'white',
        }}
        disableInnerStyle={true}
        autoFocus={autoFocus}
      />
    )
  );
};

const styles = StyleSheet.create({
  inputStyle: {
    backgroundColor: CropCircleColors.neutral[200],
    borderWidth: 0,
    borderRadius: 8,
  },
});
